import React, { useEffect, useState } from "react";
import { Loader2, Inbox, Send, Printer, FileSignature, CheckCircle2, Clock } from "lucide-react";
import api, { money, openPdf } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { SignDialog } from "../components/SignaturePad";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useSortable } from "../lib/sortable";

const StatusBadge = ({ s }) => s === "firmado"
  ? <Badge className="rounded-sm bg-green-100 text-green-700 gap-1"><CheckCircle2 className="h-3 w-3" />Firmado</Badge>
  : <Badge variant="outline" className="rounded-sm gap-1 text-amber-600 border-amber-300"><Clock className="h-3 w-3" />Pendiente</Badge>;

export default function Bandeja() {
  const { user } = useAuth();
  const { Th, sortRows } = useSortable();
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sign, setSign] = useState(null);
  const load = () => {
    setLoading(true);
    Promise.all([api.get("/inbox/received"), api.get("/inbox/sent")])
      .then(([a, b]) => { setReceived(a.data); setSent(b.data); }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const viewPdf = async (s) => { if (s.doc_type === "payroll") { try { await openPdf(`/payroll/${s.doc_id}/pdf`); } catch { toast.error("No se pudo abrir el PDF"); } } };
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const Row = ({ s, box }) => (
    <tr className="border-t border-border" data-testid={`inbox-${box}-${s.doc_number}`}>
      <td className="px-4 py-2.5 font-mono text-xs font-bold text-primary">{s.doc_number}</td>
      <td className="px-4 py-2.5">{s.title || s.doc_type}</td>
      <td className="px-4 py-2.5 text-xs">{box === "received" ? s.from_user_name : s.to_user_name}</td>
      <td className="px-4 py-2.5 text-right font-mono">{money(s.currency, s.amount)}</td>
      <td className="px-4 py-2.5"><StatusBadge s={s.status} /></td>
      <td className="px-4 py-2.5 text-right space-x-2">
        {s.doc_type === "payroll" && <Button size="sm" variant="outline" className="rounded-sm gap-1 text-xs" onClick={() => viewPdf(s)} data-testid={`inbox-pdf-${s.doc_number}`}><Printer className="h-3.5 w-3.5" />PDF</Button>}
        {box === "received" && s.doc_type === "payroll" && s.status !== "firmado" && s.to_user_id === user?.id &&
          <Button size="sm" className="rounded-sm gap-1 text-xs" onClick={() => setSign(s)} data-testid={`inbox-sign-${s.doc_number}`}><FileSignature className="h-3.5 w-3.5" />Firmar</Button>}
      </td>
    </tr>
  );

  const Grid = ({ list, box, empty }) => (
    <Card className="rounded-sm border shadow-none overflow-hidden">
      {list.length === 0 ? <div className="py-16 text-center text-muted-foreground text-sm">{empty}</div> :
      <div className="overflow-auto"><table className="w-full text-sm">
        <thead><tr className="bg-muted text-left text-xs uppercase text-muted-foreground">
          <Th field="doc_number">No.</Th><Th field="title">Documento</Th>
          <th className="px-4 py-3 font-bold">{box === "received" ? "De" : "Para"}</th>
          <Th field="amount" align="right" className="text-right">Monto</Th><Th field="status">Estado</Th>
          <th className="px-4 py-3 font-bold text-right">Acciones</th></tr></thead>
        <tbody>{sortRows(list).map((s) => <Row key={s.id} s={s} box={box} />)}</tbody>
      </table></div>}
    </Card>
  );

  return (
    <div className="space-y-5" data-testid="bandeja">
      <div>
        <h1 className="text-2xl font-heading font-bold">Bandeja de Documentos</h1>
        <p className="text-sm text-muted-foreground">Documentos que te enviaron y los que has enviado.</p>
      </div>
      <Tabs defaultValue="received" className="space-y-4">
        <TabsList className="rounded-sm">
          <TabsTrigger value="received" className="rounded-sm gap-1" data-testid="tab-received"><Inbox className="h-4 w-4" />Recibidos</TabsTrigger>
          <TabsTrigger value="sent" className="rounded-sm gap-1" data-testid="tab-sent"><Send className="h-4 w-4" />Enviados</TabsTrigger>
        </TabsList>
        <TabsContent value="received"><Grid list={received} box="received" empty="No tienes documentos recibidos." /></TabsContent>
        <TabsContent value="sent"><Grid list={sent} box="sent" empty="No has enviado documentos." /></TabsContent>
      </Tabs>
      <SignDialog open={!!sign} onOpenChange={(o) => !o && setSign(null)} docId={sign?.doc_id} docNumber={sign?.doc_number} onSigned={load} />
    </div>
  );
}
